import { AriaAttributes, ChangeEvent, FC, PropsWithChildren, ReactNode, useId, useRef } from 'react'
import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { ButtonStyleProps, theme } from '@dtx-company/shared-components/src'
import { Divider } from '@dtx-company/shared-components/src/components/atoms/Divider/index'
import { HiddenInput } from '@dtx-company/shared-components/src/components/organisms/ImageEdit/styles'
import { Icon } from '@dtx-company/shared-components/src/components/atoms/Icon/index'
import { ResponsiveBox } from '../../../components/utils/responsive-box'
import { SecondaryButton } from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { getExtensionFromFileName, isValidFileExtension } from '@app/common/src/utils/utils'
import { sendErrorNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { trashCanBold, uploadBold } from '../../../assets'
import { useUploadFileButtonState } from './hooks'
import StepHeader from '../SimplifiedDesktop/StepHeader'
import events from '@dtx-company/inter-app/src/event-tracking/events/homepage'
import styled from 'styled-components'

export const GeneratorSectionHeading: FC<PropsWithChildren<unknown>> = ({ children }) => (
  <Text variant="body/large" fontWeight={600}>
    {children}
  </Text>
)

export const GeneratorSectionCaption: FC<
  PropsWithChildren<{
    id?: string
  }>
> = ({ children, id }) => (
  <Text variant="body/medium" color="primary.grey" id={id}>
    {children}
  </Text>
)

const OrWrapper = styled(Box)`
  width: 100%;
  @media (min-width: 768px) {
    width: auto;
  }
`

const ResponsiveDivider: FC = () => (
  <ResponsiveBox width="100%" mobile="block" tablet="none">
    <Divider />
  </ResponsiveBox>
)

export const OrDivider = (): JSX.Element => (
  <OrWrapper display="flex" alignItems="center" width="100%">
    <ResponsiveDivider />
    <Text
      textAlign="center"
      mx="12px"
      lineHeight="normal"
      color="primary.grey"
      variant="general/captionAndLabel"
    >
      OR
    </Text>
    <ResponsiveDivider />
    <Box />
  </OrWrapper>
)

interface UploadLogoButtonProps extends ButtonStyleProps, AriaAttributes {
  handleChange: (file: File | null) => void
  clearFile: () => void
  fileName: string
  uploadButtonLabel?: string
}

// TODO - consolidate w/ upload file component - DF
export const UploadLogoButton: FC<UploadLogoButtonProps> = ({
  handleChange,
  'aria-labelledby': ariaLabelledBy,
  uploadButtonLabel = 'Upload',
  ...props
}) => {
  const fileUploadRef = useRef<HTMLInputElement>(null)
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0] || null
    if (file && !isValidFileExtension(file)) {
      const extension = getExtensionFromFileName(file.name)
      sendErrorNotification(`The ${extension} file type is not supported.`)
      return
    }
    if (!file && fileUploadRef.current) {
      fileUploadRef.current.value = ''
    }
    handleChange(file)
  }
  const onClear = (): void => {
    //reset the input. Fix the bug "when I uploaded a logo and clicked “clear”, i tried to upload the logo again but it couldn't"
    if (fileUploadRef.current) fileUploadRef.current.value = ''
    props.clearFile()
  }

  return (
    <Box role="group" aria-labelledby={ariaLabelledBy}>
      {props.fileName ? (
        <Box width="100%">
          <SecondaryButtonWrapper type="button" onClick={() => fileUploadRef.current?.click()}>
            <Icon src={uploadBold} height="20px" alt="" />
            <Spacer mr="8px" />
            Change
          </SecondaryButtonWrapper>
          <Spacer mr="16px" />
          <SecondaryButtonWrapper
            type="button"
            onClick={onClear}
            borderColor="primary.grey"
            color="primary.grey"
          >
            <Icon src={trashCanBold} height="20px" alt="" />
            <Spacer mr="8px" />
            Clear
          </SecondaryButtonWrapper>
        </Box>
      ) : (
        <Box flexDirection="column">
          <SecondaryButtonWrapper
            type="button"
            onClick={() => {
              fileUploadRef.current?.click()
              events.userClickedUploadALogoButton()
            }}
          >
            <Icon src={uploadBold} height="20px" alt="" />
            <Spacer mr="8px" />
            {uploadButtonLabel}
          </SecondaryButtonWrapper>
          <Text variant="input/label">Max file size: 5MB</Text>
        </Box>
      )}
      <HiddenInput
        type="file"
        ref={fileUploadRef}
        onChange={handleFileChange}
        accept="image/jpeg,image/png,image/svg"
      />
    </Box>
  )
}

export const FileUploadWarningText: FC = () => (
  <Text
    width="100%"
    textAlign="left"
    lineHeight="normal"
    color="secondary.placeholder"
    variant="input/label"
    flexBasis="100%"
  >
    Max file size for file uploads is 20MB
  </Text>
)

export const SecondaryButtonWrapper = styled(SecondaryButton)`
  width: 100%;

  @media (min-width: 1152px) {
    width: max-content;
    flex-shrink: 0;
  }
`

export const UploadFileButton: FC<{ onClick?: () => void }> = ({ onClick }) => {
  const { file, addFileUpload, removeFileUpload, handleFileChange, fileUploadRef } =
    useUploadFileButtonState()

  return (
    <>
      {file ? (
        <Box width="100%" justifyContent="space-between">
          <SecondaryButtonWrapper onClick={addFileUpload} data-heap-id="UploadFile_ChangeButton">
            <Icon src={uploadBold} height="20px" alt="" />
            <Spacer mr="8px" />
            Change
          </SecondaryButtonWrapper>
          <Spacer mr="16px" />
          <SecondaryButtonWrapper
            data-heap-id="UploadFile_ClearButton"
            onClick={removeFileUpload}
            borderColor="primary.grey"
            color="primary.grey"
          >
            <Icon src={trashCanBold} height="20px" alt="" />
            <Spacer mr="8px" />
            Clear
          </SecondaryButtonWrapper>
        </Box>
      ) : (
        <SecondaryButtonWrapper
          onClick={() => {
            addFileUpload()
            onClick?.()
          }}
          data-heap-id="UploadFile_IconButton"
        >
          <Icon src={uploadBold} height="20px" alt="" />
          <Spacer mr="8px" />
          Upload a file
        </SecondaryButtonWrapper>
      )}
      <HiddenInput type="file" ref={fileUploadRef} onChange={handleFileChange} />
    </>
  )
}

export const StepContainer: FC<{
  number: number
  borderBottom?: boolean
  borderTop?: boolean
  numberFillColor?: string
  numberBorderColor?: string
  numberColor?: string
  render?: (titleId: string) => ReactNode
  subTitle?: string
  title: string
}> = ({
  number,
  borderBottom,
  borderTop,
  numberFillColor,
  numberBorderColor,
  numberColor,
  render,
  subTitle,
  title
}) => {
  const titleId = useId()
  return (
    <Box position="relative">
      <Box flexDirection="column" alignItems="center" mr="8px" width="24px">
        {borderTop && (
          <Box
            borderLeft={`1px ${theme.colors.primary.grey} solid`}
            height="100%"
            position="absolute"
            bottom="100%"
          />
        )}
        <Box
          width="24px"
          height="24px"
          border={`1px ${numberBorderColor || theme.colors.primary.black} solid`}
          alignItems="center"
          justifyContent="center"
          borderRadius="50%"
          flexShrink={0}
          backgroundColor={numberFillColor || 'none'}
        >
          <span
            style={{
              color: numberColor || theme.colors.primary.black
            }}
          >
            {number}
          </span>
        </Box>
        {borderBottom && (
          <Box borderLeft={`1px ${theme.colors.primary.grey} solid`} height="100%" />
        )}
      </Box>
      <Box flexDirection="column" width="100%">
        <StepHeader subTitle={subTitle} title={title} id={titleId} />
        {render?.(titleId)}
      </Box>
    </Box>
  )
}
